/**
 * The event-start Live Activity, JS side (2026-10-23 owner direction).
 *
 * "Remind me" on an event used to schedule a local notification at the
 * start. On an iPhone that can do it (iOS 17.2+, Live Activities on for
 * Nexus Dex, a push-to-start token on file), the reminder is now a Lock
 * Screen / Dynamic Island countdown that appears an HOUR before the start.
 * On Android the same card is an ongoing notification the phone schedules
 * itself (the native LiveActivities plugin arms alarms; no server push),
 * so the flow below branches on the platform once:
 *
 *   - the reminder is synced to the account (PUT /api/events/:id/reminder);
 *     the Worker sends the start push at start minus an hour in THIS
 *     device's zone (cloudflare lib/liveActivities), flips it to "on now"
 *     at the start and ends it at the end;
 *   - inside the hour the app is open, so the card starts right here
 *     (plugin startLocal) and its update token is reported for the flips;
 *   - the Pokémon's art is parked in the App Group for the card
 *     (cacheEventSprite), since a widget cannot fetch over the network.
 *
 * Tokens come from the native LiveActivities plugin
 * (ios/App/App/Plugins/LiveActivities): the push-to-start token for the
 * device (rotates; always re-sent) and each activity's own token.
 * Everywhere else (Android, web, older iOS, no token yet) the caller falls
 * back to the local notification it always had.
 */
import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import { officialArtworkUrl } from "@shared/spriteCdn";
import { getPlatform } from "./platform";
import { apiRequest, hasAuthToken } from "./queryClient";
import { deviceTimeZone } from "./eventsLiveKey";
import { eventCountdownDate } from "./calendarLinks";
import { requestNotificationPermission } from "./notifications";

/** How long before the start the countdown appears (mirrors the Worker). */
export const LIVE_ACTIVITY_LEAD_MS = 60 * 60_000;

export interface LiveActivityEventInput {
  id: string;
  title: string;
  /** Local wall-clock start / end ("YYYY-MM-DDTHH:mm[:ss]"). */
  start?: string | null;
  end?: string | null;
  category?: string;
  spawns?: ReadonlyArray<{ name?: string; speciesId?: number; shinyBoost?: boolean }>;
  bonuses?: ReadonlyArray<string>;
}

interface SupportInfo {
  supported: boolean;
  enabled: boolean;
  pushToStart: boolean;
  /** "android" from the Android plugin; absent on iOS. */
  platform?: string;
}

interface LiveActivitiesPlugin {
  isSupported(): Promise<SupportInfo>;
  start(): Promise<{ observing: boolean; pushToStartToken?: string }>;
  startLocal(opts: {
    eventId: string;
    title: string;
    subtitle: string;
    bonus: string;
    tag: string;
    category: string;
    startAt: number;
    endAt: number;
    leadMs?: number;
  }): Promise<{ activityId: string }>;
  /** Android only: remember the event and arm the alarms (`leadMs` before the start; an hour when absent). */
  schedule(opts: {
    eventId: string;
    title: string;
    subtitle: string;
    bonus: string;
    tag: string;
    category: string;
    startAt: number;
    endAt: number;
    leadMs?: number;
  }): Promise<{ scheduled: boolean; mode: "local" | "alarm" }>;
  /** Either a URL (downloaded natively, no CORS) or a base64 data URL. */
  cacheSprite(opts: { eventId: string; url?: string; dataUrl?: string }): Promise<{ path: string; bytes: number }>;
  listActive(): Promise<{ activities: Array<{ activityId: string; eventId: string; phase: string }> }>;
  endAll(): Promise<{ ended: number }>;
  addListener(event: "pushToStartToken", cb: (d: { token: string }) => void): Promise<PluginListenerHandle>;
  addListener(
    event: "activityToken",
    cb: (d: { activityId: string; eventId: string; token: string; startAt?: number; endAt?: number }) => void,
  ): Promise<PluginListenerHandle>;
}

const DEVICE_FLAG_KEY = "nexus.liveActivity.device.v1";

let plugin: LiveActivitiesPlugin | null | undefined;
let support: Promise<SupportInfo> | null = null;
let listening = false;
let lastPushToStartToken: string | null = null;

function nativePlugin(): LiveActivitiesPlugin | null {
  if (plugin !== undefined) return plugin;
  try {
    const p = getPlatform();
    plugin = (p === "ios" || p === "android") && Capacitor.isPluginAvailable("LiveActivities")
      ? registerPlugin<LiveActivitiesPlugin>("LiveActivities")
      : null;
  } catch {
    plugin = null;
  }
  return plugin;
}

function isAndroid(): boolean {
  return getPlatform() === "android";
}

/** What this device can do, asked once. */
export function probeLiveActivities(): Promise<SupportInfo> {
  if (support) return support;
  const p = nativePlugin();
  support = p
    ? p.isSupported().catch(() => ({ supported: false, enabled: false, pushToStart: false }))
    : Promise.resolve({ supported: false, enabled: false, pushToStart: false });
  return support;
}

/** The push-to-start token is on file with the account (this install). */
export function hasLiveActivityDevice(): boolean {
  if (lastPushToStartToken) return true;
  try {
    return localStorage.getItem(DEVICE_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

async function registerDeviceToken(token: string): Promise<void> {
  lastPushToStartToken = token;
  if (!hasAuthToken()) return;
  try {
    await apiRequest("PUT", "/api/live-activities/device", { pushToStartToken: token, timeZone: deviceTimeZone() ?? undefined });
    try {
      localStorage.setItem(DEVICE_FLAG_KEY, "1");
    } catch {
      /* private mode */
    }
  } catch (err) {
    console.warn("[liveActivities] device register failed", err);
  }
}

async function reportActivityToken(d: { activityId: string; eventId: string; token: string; startAt?: number; endAt?: number }): Promise<void> {
  if (!hasAuthToken()) return;
  try {
    await apiRequest("PUT", "/api/live-activities/token", {
      eventId: d.eventId,
      activityId: d.activityId,
      token: d.token,
      ...(d.startAt && d.endAt ? { startAt: d.startAt, endAt: d.endAt } : {}),
    });
  } catch (err) {
    console.warn("[liveActivities] token report failed", err);
  }
}

/**
 * Begin listening for tokens and keep the account's copy current. Called on
 * launch / sign-in (PushLifecycle); idempotent; a no-op off iOS.
 */
export async function startLiveActivityListeners(): Promise<void> {
  const p = nativePlugin();
  if (!p) return;
  const info = await probeLiveActivities();
  if (!info.supported) return;
  if (!listening) {
    listening = true;
    try {
      await p.addListener("pushToStartToken", (d) => void registerDeviceToken(d.token));
      await p.addListener("activityToken", (d) => void reportActivityToken(d));
    } catch (err) {
      console.warn("[liveActivities] listeners failed", err);
    }
  }
  try {
    const r = await p.start();
    if (r.pushToStartToken) await registerDeviceToken(r.pushToStartToken);
  } catch (err) {
    console.warn("[liveActivities] start failed", err);
  }
}

/**
 * Can "Remind me" be a Live Activity on this device right now? iOS with the
 * card supported and switched on, signed in (the reminder lives on the
 * account), and either a push-to-start token on file (the hour-before push)
 * or at least the local start for an event inside the hour.
 */
export async function liveActivityReminderAvailable(
  startAtMs?: number,
  now: number = Date.now(),
  leadMs: number = LIVE_ACTIVITY_LEAD_MS,
): Promise<boolean> {
  if (!nativePlugin()) return false;
  const info = await probeLiveActivities();
  if (!info.supported) return false;
  // Android schedules the card itself: no account, no token, only the
  // notification permission, which scheduleLiveActivityReminder asks for.
  if (isAndroid()) return true;
  if (!hasAuthToken() || !info.enabled) return false;
  if (info.pushToStart && hasLiveActivityDevice()) return true;
  // No push path: only an event inside the lead can be started here and now.
  return startAtMs != null && startAtMs - now <= leadMs;
}

const KIND_TITLES: Record<string, string> = {
  spotlight: "Spotlight Hour",
  "raid-hour": "Raid Hour",
  "raid-day": "Raid Day",
  "community-day": "Community Day",
  "max-monday": "Max Monday",
  "max-battle": "Max Battle",
};
const KIND_WORDS = /\b(spotlight hour|raid hour|raid day|community day|max monday|max battle day|max battle|during)\b/gi;

/** The card's fixed text, the same shape the Worker builds (lib/liveActivities attributesFor). */
export function liveActivityAttributes(ev: LiveActivityEventInput): {
  eventId: string;
  title: string;
  subtitle: string;
  bonus: string;
  tag: string;
  category: string;
} {
  const category = ev.category ?? "event";
  const names = (ev.spawns ?? []).map((s) => (s.name ?? "").trim()).filter((n) => n.length > 0);
  const kindTitle = KIND_TITLES[category];
  const title = kindTitle ?? (ev.title.replace(/^Pokémon GO\s+/i, "").trim() || ev.title);
  const subtitle = kindTitle
    ? names.length > 0
      ? names.slice(0, 2).join(" & ")
      : ev.title.replace(KIND_WORDS, " ").replace(/\s+/g, " ").trim()
    : names.slice(0, 3).join(", ");
  const name = ev.title.toLowerCase();
  const tag = /\bmega\b/.test(name)
    ? "MEGA"
    : category === "max-battle" || category === "max-monday" || /\b(dynamax|gigantamax|max battle)\b/.test(name)
      ? "DYNAMAX"
      : (ev.spawns ?? []).some((s) => s.shinyBoost)
        ? "SHINY"
        : "";
  return {
    eventId: ev.id,
    title: title.slice(0, 40),
    subtitle: subtitle.slice(0, 60),
    bonus: (ev.bonuses?.[0] ?? "").slice(0, 40),
    tag,
    category,
  };
}

/** The event's local window in ms, or null without a usable start. */
export function liveActivityWindow(ev: LiveActivityEventInput): { startAt: number; endAt: number } | null {
  if (!ev.start) return null;
  const start = eventCountdownDate(ev.start).getTime();
  if (Number.isNaN(start)) return null;
  const endRaw = ev.end ? eventCountdownDate(ev.end).getTime() : Number.NaN;
  const endAt = Number.isNaN(endRaw) || endRaw <= start ? start + 60 * 60_000 : endRaw;
  return { startAt: start, endAt };
}

/**
 * Park the first Pokémon's art for the card. The native side downloads it
 * (a WebView fetch is blocked by CORS on the sprite CDN). Best-effort,
 * never throws.
 */
export async function cacheEventSprite(ev: LiveActivityEventInput): Promise<boolean> {
  const p = nativePlugin();
  const speciesId = (ev.spawns ?? []).find((s) => typeof s.speciesId === "number" && s.speciesId >= 1)?.speciesId;
  if (!p || !speciesId) return false;
  try {
    const shiny = (ev.spawns ?? []).find((s) => s.speciesId === speciesId)?.shinyBoost === true;
    await p.cacheSprite({ eventId: ev.id, url: officialArtworkUrl(speciesId, { shiny }) });
    return true;
  } catch {
    return false;
  }
}

export type LiveActivityReminderResult = { ok: true; mode: "push" | "local" } | { ok: false; detail: string };

/**
 * Set the reminder as a Live Activity: the account's reminder row for the
 * Worker's hour-before push, the sprite for the card, and the card itself
 * at once when the event is already inside the hour.
 */
export async function scheduleLiveActivityReminder(
  ev: LiveActivityEventInput,
  now: number = Date.now(),
  opts: {
    /** How long before the start the card appears (lib/liveEventPrefs); an hour by default. */
    leadMs?: number;
    /**
     * The account already covers this event (a standing subscription,
     * migration 0110): the Worker starts the iPhone card from that, so no
     * per-event reminder row is written.
     */
    accountCovered?: boolean;
  } = {},
): Promise<LiveActivityReminderResult> {
  const p = nativePlugin();
  const window = liveActivityWindow(ev);
  const leadMs = Math.max(0, opts.leadMs ?? LIVE_ACTIVITY_LEAD_MS);
  if (!p || !window) return { ok: false, detail: "No usable start time." };
  if (isAndroid()) {
    // The phone keeps the reminder and draws the card at the hour-before
    // mark on its own; the only gate is the notification permission.
    let perm: string = "unknown";
    try {
      perm = await requestNotificationPermission({ reprompt: true });
    } catch {
      perm = "unknown";
    }
    if (perm !== "granted") return { ok: false, detail: "Notifications are off for Nexus Dex." };
    try {
      await Promise.race([cacheEventSprite(ev), new Promise((r) => setTimeout(r, 1500))]);
      const r = await p.schedule({ ...liveActivityAttributes(ev), startAt: window.startAt, endAt: window.endAt, leadMs });
      return { ok: true, mode: r.mode === "local" ? "local" : "push" };
    } catch (err) {
      return { ok: false, detail: err instanceof Error ? err.message : String(err) };
    }
  }
  if (!opts.accountCovered) {
    try {
      await apiRequest("PUT", `/api/events/${encodeURIComponent(ev.id)}/reminder`, { reminder: true });
    } catch (err) {
      return { ok: false, detail: `Could not save the reminder: ${err instanceof Error ? err.message : String(err)}` };
    }
  }
  void cacheEventSprite(ev);
  if (window.startAt - now > leadMs) {
    if (!hasLiveActivityDevice()) return { ok: false, detail: "This phone cannot be woken for the countdown yet." };
    return { ok: true, mode: "push" };
  }
  try {
    // Give the sprite a moment to land so the first draw has it.
    await Promise.race([cacheEventSprite(ev), new Promise((r) => setTimeout(r, 1500))]);
    await p.startLocal({ ...liveActivityAttributes(ev), startAt: window.startAt, endAt: window.endAt });
    return { ok: true, mode: "local" };
  } catch (err) {
    return { ok: false, detail: err instanceof Error ? err.message : String(err) };
  }
}

/** Preview the card with a made-up event an hour away (Settings diagnostics). */
export async function previewLiveActivity(
  now: number = Date.now(),
  leadMs: number = LIVE_ACTIVITY_LEAD_MS,
): Promise<{ ok: boolean; detail: string }> {
  const p = nativePlugin();
  if (!p) return { ok: false, detail: "The countdown card needs the phone app." };
  let info = await probeLiveActivities();
  if (!info.supported) return { ok: false, detail: "This iPhone cannot show Live Activities (iOS 16.2 or newer)." };
  if (isAndroid() && !info.enabled) {
    // The card is a notification on Android: ask like the reminder does.
    try {
      await requestNotificationPermission({ reprompt: true });
    } catch {
      /* the re-probe below says what happened */
    }
    support = null;
    info = await probeLiveActivities();
  }
  if (!info.enabled) {
    return {
      ok: false,
      detail: isAndroid()
        ? "Notifications are off for Nexus Dex in your phone's Settings."
        : "Live Activities are off for Nexus Dex in your phone's Settings.",
    };
  }
  try {
    // Pikachu's art for the card, so the preview looks like the real thing.
    const eventId = `preview-${now}`;
    await Promise.race([
      cacheEventSprite({ id: eventId, title: "Spotlight Hour", spawns: [{ speciesId: 25, name: "Pikachu", shinyBoost: true }] }),
      new Promise((r) => setTimeout(r, 1500)),
    ]);
    await p.startLocal({
      eventId,
      title: "Spotlight Hour",
      subtitle: "Pikachu",
      bonus: "2× Catch Stardust",
      tag: "SHINY",
      category: "spotlight",
      // The pretend event starts one lead from now, so the card shows the
      // countdown the trainer chose; with no countdown it is on already.
      startAt: now + Math.max(0, leadMs),
      endAt: now + Math.max(0, leadMs) + LIVE_ACTIVITY_LEAD_MS,
      leadMs: Math.max(0, leadMs),
    });
    const minutes = Math.round(Math.max(0, leadMs) / 60_000);
    const what =
      minutes === 0
        ? "a pretend Spotlight Hour, on now"
        : minutes === 60
          ? "a one-hour countdown to a pretend Spotlight Hour"
          : `a ${minutes}-minute countdown to a pretend Spotlight Hour`;
    return {
      ok: true,
      detail: isAndroid() ? `In your notifications now: ${what}.` : `On your Lock Screen now: ${what}.`,
    };
  } catch (err) {
    return { ok: false, detail: err instanceof Error ? err.message : String(err) };
  }
}

/** Tests only. */
export function __resetLiveActivitiesForTest(): void {
  plugin = undefined;
  support = null;
  listening = false;
  lastPushToStartToken = null;
  try {
    localStorage.removeItem(DEVICE_FLAG_KEY);
  } catch {
    /* ignore */
  }
}
